import { FC, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { produtoService } from '@/services/produtoService'
import { CONFIRMACAO_RETIRADA, mensagemErroProdutoP1 } from '@/constants/catalogoProdutoP1'
import type { Produto, StatusProduto } from '@/types/produto'
import {
  CODIGO_OUTRA_NECESSIDADE,
  ROTULOS_STATUS_PRODUTO,
  rotuloStatusProduto,
} from '@/constants/codigosProduto'

/**
 * Status normativo do catálogo (AN-PP1 §2): chip de leitura e ação ADMIN "Alterar status".
 * O status é dado do backend — a tela só exibe e envia; nada é derivado aqui.
 */

const COR_STATUS: Record<StatusProduto, ChipProps['color']> = {
  ATIVA: 'success',
  ATIVA_COM_ANALISE: 'info',
  SEM_POLITICA_SIMULACAO: 'warning',
  RETIRADA: 'default',
}

/** O que cada status significa (AN-PP1 §2.1) — tooltip do chip. */
export const DESCRICAO_STATUS_PRODUTO: Record<StatusProduto, string> = {
  ATIVA: 'Produto ativo no catálogo.',
  ATIVA_COM_ANALISE: 'Produto ativo; cada reserva passa por avaliação da Originei.',
  SEM_POLITICA_SIMULACAO:
    'Sem política comercial de simulação vigente: a simulação não calcula comissão para este produto.',
  RETIRADA: 'Produto retirado do catálogo: não aceita novas reservas nem aparece para consultores. Status terminal.',
}

const statusConhecido = (s: string | null | undefined): s is StatusProduto =>
  !!s && Object.prototype.hasOwnProperty.call(ROTULOS_STATUS_PRODUTO, s)

interface ChipStatusProdutoProps {
  status?: StatusProduto | null
  size?: 'small' | 'medium'
  sx?: ChipProps['sx']
}

/** Chip do status. Sem status (backend anterior à V13) não renderiza nada. */
export const ChipStatusProduto: FC<ChipStatusProdutoProps> = ({ status, size = 'small', sx }) => {
  if (!status) return null
  const conhecido = statusConhecido(status)
  return (
    <Tooltip title={conhecido ? DESCRICAO_STATUS_PRODUTO[status] : ''}>
      <Chip
        size={size}
        label={rotuloStatusProduto(status)}
        color={conhecido ? COR_STATUS[status] : 'default'}
        variant={status === 'RETIRADA' ? 'outlined' : 'filled'}
        aria-label={`Status do catálogo: ${rotuloStatusProduto(status)}`}
        sx={sx}
      />
    </Tooltip>
  )
}

const STATUS_OPCOES = Object.keys(ROTULOS_STATUS_PRODUTO) as StatusProduto[]

/** Motivo de não poder alterar (null = pode). Regras do servidor espelhadas só para guiar a tela. */
export const motivoStatusBloqueado = (produto: Pick<Produto, 'codigo' | 'status'>): string | null => {
  if (!produto.status) return 'Status ainda não disponível para este produto.'
  if (produto.status === 'RETIRADA') return 'Produto retirado: o status é terminal.'
  if (produto.codigo === CODIGO_OUTRA_NECESSIDADE) return 'O status de "Outra necessidade" é fixo.'
  return null
}

interface AlterarProps {
  produto: Produto
  onAlterado: (produto: Produto) => void
}

/**
 * Botão + diálogo "Alterar status" (só ADMIN — quem renderiza decide; o servidor recusa os
 * demais com 403). Motivo sempre obrigatório; RETIRADA pede a digitação de "RETIRAR" e avisa
 * que é terminal.
 */
export const AlterarStatusProduto: FC<AlterarProps> = ({ produto, onAlterado }) => {
  const [aberto, setAberto] = useState(false)
  const [destino, setDestino] = useState<StatusProduto | ''>('')
  const [motivo, setMotivo] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const bloqueio = motivoStatusBloqueado(produto)
  const retirando = destino === 'RETIRADA'
  const podeConfirmar =
    !!destino &&
    destino !== produto.status &&
    !!motivo.trim() &&
    (!retirando || confirmacao.trim() === CONFIRMACAO_RETIRADA)

  const abrir = () => {
    setDestino('')
    setMotivo('')
    setConfirmacao('')
    setErro(null)
    setAberto(true)
  }

  const confirmar = async () => {
    if (!podeConfirmar || !destino) return
    setSalvando(true)
    setErro(null)
    try {
      const atualizado = await produtoService.alterarStatus(produto.id, {
        status: destino,
        motivo: motivo.trim(),
        ...(retirando && { confirmacao: confirmacao.trim() }),
      })
      // Sem corpo na resposta: reflete o que foi pedido (retirar também desliga `ativo`, §2.2).
      onAlterado(
        atualizado && typeof atualizado === 'object'
          ? atualizado
          : { ...produto, status: destino, ...(retirando && { ativo: false }) }
      )
      setAberto(false)
    } catch (e) {
      setErro(mensagemErroProdutoP1(e, 'Não foi possível alterar o status do produto.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <>
      <Tooltip title={bloqueio ?? ''}>
        <span>
          <Button variant="outlined" size="small" onClick={abrir} disabled={!!bloqueio}>
            Alterar status
          </Button>
        </span>
      </Tooltip>
      <Dialog open={aberto} onClose={salvando ? undefined : () => setAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Alterar status do produto</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
          <Typography variant="body2" color="text.secondary">
            "{produto.nome}" está como <strong>{rotuloStatusProduto(produto.status)}</strong>. A mudança é
            auditada com o motivo informado.
          </Typography>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField
            select
            label="Novo status"
            value={destino}
            onChange={(e) => {
              setDestino(e.target.value as StatusProduto)
              setConfirmacao('')
            }}
            fullWidth
            required
          >
            {STATUS_OPCOES.filter((s) => s !== produto.status).map((s) => (
              <MenuItem key={s} value={s}>
                {ROTULOS_STATUS_PRODUTO[s]}
              </MenuItem>
            ))}
          </TextField>
          {retirando && (
            <Alert severity="warning">
              A retirada é <strong>definitiva</strong>: o produto sai do catálogo dos consultores, deixa de aceitar
              novas reservas e as políticas de parceiro, perguntas, campos de diagnóstico e conteúdos dele são
              desativados. Não há como voltar. Reservas existentes seguem como histórico.
            </Alert>
          )}
          <TextField
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={2}
            fullWidth
            required
          />
          {retirando && (
            <TextField
              label={`Digite ${CONFIRMACAO_RETIRADA} para confirmar`}
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              fullWidth
              required
              autoComplete="off"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={retirando ? 'error' : 'primary'}
            onClick={() => void confirmar()}
            disabled={salvando || !podeConfirmar}
          >
            {salvando ? 'Salvando…' : retirando ? 'Retirar produto' : 'Alterar status'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
